import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api, { getApiBase } from '../utils/api';
import toast from 'react-hot-toast';

export default function Settings() {
  const { user, refetchUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState('profile');
  const [form, setForm] = useState({ name:'', company_name:'', company_address:'', company_phone:'', company_email:'', company_gst:'', signer_name:'', signer_desig:'', password:'', confirm_password:'', inv_prefix:'INV', inv_year:1, inv_digits:3, quo_prefix:'QUO', quo_year:1, quo_digits:3, ren_prefix:'REN', ren_year:1, ren_digits:3 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo:false, sig:false });
  const logoRef = useRef(); const sigRef = useRef();

  useEffect(() => {
    if (user) setForm(f => ({
      ...f, name:user.name||'', company_name:user.company_name||'', company_address:user.company_address||'',
      company_phone:user.company_phone||'', company_email:user.company_email||'', company_gst:user.company_gst||'',
      signer_name:user.signer_name||'', signer_desig:user.signer_desig||'',
      inv_prefix:user.inv_prefix||'INV', inv_year:parseInt(user.inv_year??1), inv_digits:parseInt(user.inv_digits||3),
      quo_prefix:user.quo_prefix||'QUO', quo_year:parseInt(user.quo_year??1), quo_digits:parseInt(user.quo_digits||3),
      ren_prefix:user.ren_prefix||'REN', ren_year:parseInt(user.ren_year??1), ren_digits:parseInt(user.ren_digits||3),
    }));
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    if (form.password && form.password !== form.confirm_password) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      const payload = { name:form.name };
      if (isAdmin) Object.assign(payload, {
        company_name:form.company_name, company_address:form.company_address, company_phone:form.company_phone,
        company_email:form.company_email, company_gst:form.company_gst, signer_name:form.signer_name, signer_desig:form.signer_desig,
        inv_prefix:form.inv_prefix, inv_year:form.inv_year, inv_digits:form.inv_digits,
        quo_prefix:form.quo_prefix, quo_year:form.quo_year, quo_digits:form.quo_digits,
        ren_prefix:form.ren_prefix, ren_year:form.ren_year, ren_digits:form.ren_digits,
      });
      if (form.password) payload.password = form.password;
      await api.put('/auth/profile', payload);
      await refetchUser();
      toast.success('Settings saved!');
      setForm(f=>({ ...f, password:'', confirm_password:'' }));
    } finally { setSaving(false); }
  };

  const uploadFile = async (file, type) => {
    const key = type === 'logo' ? 'logo' : 'sig';
    setUploading(u=>({...u,[key]:true}));
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post(`/auth/upload-${type}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
      await refetchUser(); toast.success(`${type === 'logo' ? 'Logo' : 'Signature'} uploaded!`);
    } finally { setUploading(u=>({...u,[key]:false})); }
  };

  const previewNum = (prefix, useYear, digits) => {
    const y = useYear ? `-${new Date().getFullYear()}` : '';
    const n = '1'.padStart(digits, '0');
    return `${prefix}${y}-${n}`;
  };

  const imgBase = getApiBase();

  return (
    <div>
      <div className="page-header">
        <div><h2 className="page-title">Settings</h2><p className="page-subtitle">Manage your account, company & preferences</p></div>
      </div>

      <div className="tabs">
        {['profile', isAdmin&&'company', isAdmin&&'branding', isAdmin&&'numbering', 'security'].filter(Boolean).map(t=>(
          <div key={t} className={`tab ${tab===t?'active':''}`} onClick={()=>setTab(t)}>
            {t.charAt(0).toUpperCase()+t.slice(1)}
          </div>
        ))}
      </div>

      <form onSubmit={save}>
        {/* ── Profile ── */}
        {tab==='profile' && (
          <div className="card">
            <div className="card-header"><span className="card-title">👤 Profile</span></div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Full Name</label>
                <input className="form-control" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Your name" /></div>
              <div className="form-group"><label className="form-label">Email (cannot change)</label>
                <input className="form-control" value={user?.email||''} disabled /></div>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ marginBottom:10 }}>App Theme</label>
              <div style={{ display:'flex',alignItems:'center',gap:14 }}>
                <button type="button" className="theme-btn" onClick={toggleTheme} style={{ fontSize:14,padding:'9px 18px' }}>
                  {theme==='dark'?'☀️ Switch to Light Mode':'🌙 Switch to Dark Mode'}
                </button>
                <span style={{ fontSize:12,color:'var(--text3)' }}>Current: <strong style={{ color:'var(--text2)' }}>{theme} mode</strong></span>
              </div>
            </div>
            <div style={{ padding:14,background:'var(--bg4)',borderRadius:'var(--r)',display:'flex',alignItems:'center',gap:12 }}>
              <div className="user-avatar" style={{ width:40,height:40,fontSize:16 }}>{user?.name?.[0]?.toUpperCase()}</div>
              <div><div style={{ fontWeight:600 }}>{user?.name}</div><div style={{ fontSize:12,color:'var(--text3)' }}>{user?.email} · <span className={`badge badge-${user?.role}`}>{user?.role}</span></div></div>
            </div>
          </div>
        )}

        {/* ── Company ── */}
        {tab==='company' && isAdmin && (
          <div className="card">
            <div className="card-header"><span className="card-title">🏢 Company Details</span></div>
            <p style={{ color:'var(--text3)',fontSize:12.5,marginBottom:18 }}>These appear on all generated PDFs.</p>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Company Name</label>
                <input className="form-control" value={form.company_name} onChange={e=>setForm({...form,company_name:e.target.value})} placeholder="Aura Design Agency" /></div>
              <div className="form-group"><label className="form-label">Company Email</label>
                <input type="email" className="form-control" value={form.company_email} onChange={e=>setForm({...form,company_email:e.target.value})} placeholder="info@company.com" /></div>
            </div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Phone</label>
                <input className="form-control" value={form.company_phone} onChange={e=>setForm({...form,company_phone:e.target.value})} placeholder="+91 98765 43210" /></div>
              <div className="form-group"><label className="form-label">GST Number</label>
                <input className="form-control" value={form.company_gst} onChange={e=>setForm({...form,company_gst:e.target.value})} placeholder="33ABCDE1234F1Z5" /></div>
            </div>
            <div className="form-group"><label className="form-label">Company Address</label>
              <textarea className="form-control" rows={3} value={form.company_address} onChange={e=>setForm({...form,company_address:e.target.value})} placeholder="Full address…" /></div>
            <hr className="divider"/>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Signatory Name (PDF)</label>
                <input className="form-control" value={form.signer_name} onChange={e=>setForm({...form,signer_name:e.target.value})} placeholder="e.g. John Doe" /></div>
              <div className="form-group"><label className="form-label">Designation (PDF)</label>
                <input className="form-control" value={form.signer_desig} onChange={e=>setForm({...form,signer_desig:e.target.value})} placeholder="e.g. Managing Director" /></div>
            </div>
          </div>
        )}

        {/* ── Branding ── */}
        {tab==='branding' && isAdmin && (
          <div className="card">
            <div className="card-header"><span className="card-title">🎨 Branding</span></div>
            <p style={{ color:'var(--text3)',fontSize:12.5,marginBottom:20 }}>Logo and signature appear in generated PDFs. Max 2MB, PNG/JPG.</p>
            <div className="grid-2">
              <div>
                <div className="form-label" style={{ marginBottom:8 }}>Company Logo</div>
                {user?.company_logo && (
                  <img src={`${imgBase}/uploads/${user.company_logo}`} alt="logo" className="upload-preview" style={{ marginBottom:10 }} />
                )}
                <div className="upload-area" onClick={()=>logoRef.current?.click()}>
                  {uploading.logo ? <span className="spinner spinner-dark"/> : <><div style={{ fontSize:28,marginBottom:6 }}>🖼️</div><div>{user?.company_logo?'Click to replace logo':'Click to upload logo'}</div><div style={{ fontSize:11,marginTop:4 }}>PNG, JPG, WEBP</div></>}
                </div>
                <input ref={logoRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e=>e.target.files[0]&&uploadFile(e.target.files[0],'logo')} />
              </div>
              <div>
                <div className="form-label" style={{ marginBottom:8 }}>Signature Image</div>
                {user?.signature_img && (
                  <img src={`${imgBase}/uploads/${user.signature_img}`} alt="signature" className="upload-preview" style={{ marginBottom:10,background:'white' }} />
                )}
                <div className="upload-area" onClick={()=>sigRef.current?.click()}>
                  {uploading.sig ? <span className="spinner spinner-dark"/> : <><div style={{ fontSize:28,marginBottom:6 }}>✍️</div><div>{user?.signature_img?'Click to replace signature':'Click to upload signature'}</div><div style={{ fontSize:11,marginTop:4 }}>PNG recommended (transparent bg)</div></>}
                </div>
                <input ref={sigRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e=>e.target.files[0]&&uploadFile(e.target.files[0],'signature')} />
              </div>
            </div>
          </div>
        )}

        {/* ── Numbering ── */}
        {tab==='numbering' && isAdmin && (
          <div className="card">
            <div className="card-header"><span className="card-title">🔢 Numbering Format</span></div>
            <p style={{ color:'var(--text3)',fontSize:12.5,marginBottom:20 }}>Configure how invoice, quotation, and renewal numbers are generated.</p>
            {[
              { label:'Invoice', prefix:'inv_prefix', year:'inv_year', digits:'inv_digits', icon:'🧾' },
              { label:'Quotation', prefix:'quo_prefix', year:'quo_year', digits:'quo_digits', icon:'📋' },
              { label:'Renewal', prefix:'ren_prefix', year:'ren_year', digits:'ren_digits', icon:'🔄' },
            ].map(cfg=>(
              <div key={cfg.label} style={{ padding:16,background:'var(--bg4)',borderRadius:'var(--r)',marginBottom:14 }}>
                <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:14 }}>
                  <span style={{ fontSize:20 }}>{cfg.icon}</span>
                  <span style={{ fontWeight:700,fontSize:14 }}>{cfg.label} Numbers</span>
                  <div className="num-preview" style={{ marginLeft:'auto' }}>{previewNum(form[cfg.prefix],form[cfg.year],form[cfg.digits])}</div>
                </div>
                <div className="grid-3">
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Prefix</label>
                    <input className="form-control" value={form[cfg.prefix]} onChange={e=>setForm({...form,[cfg.prefix]:e.target.value.toUpperCase()})} placeholder="INV" maxLength={10} />
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Include Year</label>
                    <select className="form-control" value={form[cfg.year]} onChange={e=>setForm({...form,[cfg.year]:parseInt(e.target.value)})}>
                      <option value={1}>Yes (e.g. INV-2026-001)</option>
                      <option value={0}>No (e.g. INV-001)</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Digit Padding</label>
                    <select className="form-control" value={form[cfg.digits]} onChange={e=>setForm({...form,[cfg.digits]:parseInt(e.target.value)})}>
                      {[2,3,4,5].map(n=><option key={n} value={n}>{n} digits (e.g. {'0'.repeat(n-1)}1)</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Security ── */}
        {tab==='security' && (
          <div className="card">
            <div className="card-header"><span className="card-title">🔐 Change Password</span></div>
            <p style={{ color:'var(--text3)',fontSize:12.5,marginBottom:18 }}>Leave blank to keep your current password.</p>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">New Password</label>
                <input type="password" className="form-control" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Min. 8 characters" /></div>
              <div className="form-group"><label className="form-label">Confirm Password</label>
                <input type="password" className="form-control" value={form.confirm_password} onChange={e=>setForm({...form,confirm_password:e.target.value})} placeholder="Repeat password" /></div>
            </div>
          </div>
        )}

        {tab !== 'branding' && (
          <div style={{ marginTop:18,display:'flex',justifyContent:'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner"/> : '✓ Save Changes'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
